import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';
import { CURRENCIES, tenantDefaultCurrency } from '../lib/currencies';
import { formatMoney } from '../lib/quoteTotals';
import { useAuthStore } from '../store/useAuthStore';
import { useModules } from '../lib/useModules';
import QuoteProfileForm from '../components/quotes/QuoteProfileForm';
import dayjs from 'dayjs';

const STATUS_META = {
    draft:    { label: 'Borrador',  tone: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300' },
    sent:     { label: 'Enviada',   tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    accepted: { label: 'Aceptada',  tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    rejected: { label: 'Rechazada', tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
    expired:  { label: 'Vencida',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

export default function QuotesPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const tenant = useAuthStore((s) => s.tenant);
    const { isFeatureEnabled } = useModules();
    const canEditCatalog = isFeatureEnabled('quotes', 'catalog');

    const [tab, setTab] = useState('quotes');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');

    const { data: quotesResp, isLoading } = useQuery({
        queryKey: ['quotes', statusFilter, search],
        queryFn: () => axios.get('/api/quotes', {
            params: { status: statusFilter || undefined, search: search || undefined },
        }),
    });
    const quotes = quotesResp?.data?.data || [];

    return (
        <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark">
            <Header title="Cotizaciones" />

            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-1 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl">
                        {[
                            { key: 'quotes', label: 'Cotizaciones' },
                            { key: 'catalog', label: 'Catálogo' },
                            { key: 'profile', label: 'Perfil de empresa' },
                        ].map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                                    tab === t.key
                                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === 'quotes' && (
                        <button
                            onClick={() => navigate('/quotes/new')}
                            className="py-2.5 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-lg">add</span> Nueva cotización
                        </button>
                    )}
                </div>

                {tab === 'quotes' ? (
                    <>
                        <div className="flex flex-wrap gap-2">
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar por número o cliente"
                                className="flex-1 min-w-[220px] text-sm px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white"
                            />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="text-sm px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white"
                            >
                                <option value="">Todos los estados</option>
                                {Object.entries(STATUS_META).map(([k, v]) => (
                                    <option key={k} value={k}>{v.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden">
                            {isLoading ? (
                                <p className="p-8 text-sm text-gray-400 text-center">Cargando...</p>
                            ) : quotes.length === 0 ? (
                                <div className="p-12 text-center">
                                    <span className="material-symbols-outlined text-4xl text-gray-300 block mb-2">request_quote</span>
                                    <p className="text-sm text-gray-400">Todavía no hay cotizaciones.</p>
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-slate-800/50">
                                        <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                                            <th className="px-5 py-3">Número</th>
                                            <th className="px-5 py-3">Cliente</th>
                                            <th className="px-5 py-3">Fecha</th>
                                            <th className="px-5 py-3">Estado</th>
                                            <th className="px-5 py-3 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotes.map((q) => {
                                            const meta = STATUS_META[q.status] || STATUS_META.draft;
                                            return (
                                                <tr
                                                    key={q.id}
                                                    onClick={() => navigate(`/quotes/${q.id}`)}
                                                    className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-slate-800/40 cursor-pointer"
                                                >
                                                    <td className="px-5 py-3.5 font-bold dark:text-white">{q.number}</td>
                                                    <td className="px-5 py-3.5 text-gray-600 dark:text-gray-300">
                                                        {q.contact?.full_name || q.contact?.email || <span className="text-gray-400">Sin cliente</span>}
                                                    </td>
                                                    <td className="px-5 py-3.5 text-gray-500">{dayjs(q.issue_date).format('DD/MM/YYYY')}</td>
                                                    <td className="px-5 py-3.5">
                                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.tone}`}>
                                                            {meta.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3.5 text-right font-bold dark:text-white">
                                                        {formatMoney(q.total, q.currency)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                ) : tab === 'catalog' ? (
                    <CatalogManager canEdit={canEditCatalog} tenant={tenant} qc={qc} />
                ) : (
                    <QuoteProfileForm />
                )}
            </div>
        </div>
    );
}

function CatalogManager({ canEdit, tenant, qc }) {
    const emptyForm = {
        name: '', description: '', category: '',
        unit_price: '', currency: tenantDefaultCurrency(tenant), tax_rate: 5,
    };
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);

    const { data: itemsResp, isLoading } = useQuery({
        queryKey: ['catalog-items'],
        queryFn: () => axios.get('/api/catalog-items'),
    });
    const items = itemsResp?.data || [];

    const saveMutation = useMutation({
        mutationFn: (payload) => editingId
            ? axios.put(`/api/catalog-items/${editingId}`, payload)
            : axios.post('/api/catalog-items', payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['catalog-items'] });
            setForm(emptyForm);
            setEditingId(null);
        },
        onError: (e) => alert(e.response?.data?.message || 'No se pudo guardar el ítem.'),
    });

    const deactivateMutation = useMutation({
        mutationFn: (id) => axios.delete(`/api/catalog-items/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog-items'] }),
    });

    const submit = (e) => {
        e.preventDefault();
        saveMutation.mutate({
            ...form,
            // Vacío = precio abierto: el importe se escribe al cotizar.
            unit_price: form.unit_price === '' ? null : Number(form.unit_price),
            tax_rate: Number(form.tax_rate),
        });
    };

    const grouped = items.reduce((acc, i) => {
        (acc[i.category || 'Sin categoría'] ||= []).push(i);
        return acc;
    }, {});

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {canEdit && (
                <form onSubmit={submit} className="lg:col-span-1 bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-5 space-y-3 h-fit">
                    <h3 className="font-black text-sm dark:text-white">
                        {editingId ? 'Editar ítem' : 'Nuevo ítem'}
                    </h3>

                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Nombre del servicio" className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />

                    <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={3} placeholder="Detalle (una línea por concepto incluido)"
                        className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white resize-none" />

                    <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                        placeholder="Categoría (ej. Free Zone, Impuestos)" className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />

                    <div className="grid grid-cols-3 gap-2">
                        <input type="number" step="0.01" min="0" value={form.unit_price}
                            onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                            placeholder="Precio" className="text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />
                        <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                            className="text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white">
                            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                        </select>
                        <input type="number" step="0.01" min="0" max="100" value={form.tax_rate}
                            onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                            placeholder="IVA %" className="text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />
                    </div>
                    <p className="text-[11px] text-gray-400">
                        Dejá el precio vacío si el ítem no tiene tarifa fija: se escribirá al cotizar.
                    </p>

                    <div className="flex gap-2">
                        <button type="submit" disabled={saveMutation.isPending}
                            className="py-2 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50">
                            {editingId ? 'Guardar cambios' : 'Agregar al catálogo'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }}
                                className="py-2 px-4 text-sm font-bold text-gray-500">Cancelar</button>
                        )}
                    </div>
                </form>
            )}

            <div className={canEdit ? 'lg:col-span-2 space-y-5' : 'lg:col-span-3 space-y-5'}>
                {isLoading && <p className="text-sm text-gray-400">Cargando catálogo...</p>}
                {!isLoading && items.length === 0 && (
                    <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
                        <span className="material-symbols-outlined text-4xl text-gray-300 block mb-2">inventory_2</span>
                        <p className="text-sm text-gray-400">El catálogo está vacío.</p>
                    </div>
                )}

                {Object.entries(grouped).map(([category, catItems]) => (
                    <div key={category}>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{category}</p>
                        <div className="bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 rounded-2xl divide-y divide-gray-100 dark:divide-gray-800">
                            {catItems.map((item) => (
                                <div key={item.id} className="p-4 flex items-start justify-between gap-4 group">
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold dark:text-white">{item.name}</p>
                                        {item.description && (
                                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-line">{item.description}</p>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0 flex items-center gap-3">
                                        <div>
                                            <p className="text-sm font-bold dark:text-white">
                                                {item.unit_price === null
                                                    ? <span className="text-amber-600 text-xs">Precio abierto</span>
                                                    : formatMoney(item.unit_price, item.currency)}
                                            </p>
                                            <p className="text-[10px] text-gray-400">IVA {Number(item.tax_rate)}%</p>
                                        </div>
                                        {canEdit && (
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => {
                                                        setEditingId(item.id);
                                                        setForm({
                                                            name: item.name, description: item.description || '',
                                                            category: item.category || '',
                                                            unit_price: item.unit_price === null ? '' : item.unit_price,
                                                            currency: item.currency, tax_rate: item.tax_rate,
                                                        });
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                    className="p-1.5 text-gray-400 hover:text-blue-600"
                                                >
                                                    <span className="material-symbols-outlined text-lg">edit</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`¿Quitar "${item.name}" del catálogo? Las cotizaciones que ya lo usan no cambian.`)) {
                                                            deactivateMutation.mutate(item.id);
                                                        }
                                                    }}
                                                    className="p-1.5 text-gray-400 hover:text-red-500"
                                                >
                                                    <span className="material-symbols-outlined text-lg">delete</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
